import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Briefcase, MapPin, Clock, Users, FileText } from "lucide-react";

import type { JobApplicationData, JobOpening } from "@/types";
import { buildApiUrl } from "@/lib/config";

const jobOpenings: JobOpening[] = [
  {
    id: "1",
    title: "Tour Operator",
    location: "New Delhi",
    type: "Full-time",
    experience: "2-4 years",
    department: "Travel",
    description: "Hiring Tour Operator to manage and coordinate travel packages, handle client bookings, and ensure smooth travel operations.",
    skills: ["Tour Planning", "Client Management", "Operations"]
  },
  {
    id: "2",
    title: "Forex Specialist",
    location: "New Delhi",
    type: "Full-time",
    experience: "1-3 years",
    department: "Forex",
    description: "Hiring forex specialist to handle currency exchange operations, assist customers with forex services, and maintain compliance.",
    skills: ["Currency Exchange", "Customer Service", "Compliance"]
  },
  {
    id: "3",
    title: "Travel Consultant",
    location: "New Delhi",
    type: "Full-time",
    experience: "1-2 years",
    department: "Travel",
    description: "Hiring in Travel department to provide expert travel advice, create customized itineraries, and deliver exceptional customer service.",
    skills: ["Travel Planning", "Customer Relations", "Sales"]
  },
  {
    id: "4",
    title: "Operation Executive",
    location: "New Delhi",
    type: "Full-time",
    experience: "2-3 years",
    department: "Operations",
    description: "Ticketing/Packages operations executive to handle booking operations, coordinate with vendors, and ensure smooth service delivery.",
    skills: ["Ticketing", "Package Management", "Vendor Coordination"]
  },
  {
    id: "5",
    title: "Branch Manager",
    location: "New Delhi",
    type: "Full-time",
    experience: "4-6 years",
    department: "Management",
    description: "Forex/Travel Agency branch manager to oversee daily operations, manage team performance, and drive business growth.",
    skills: ["Team Management", "Business Development", "Operations"]
  },
  {
    id: "6",
    title: "Visa & Immigration Manager",
    location: "New Delhi",
    type: "Full-time",
    experience: "3-5 years",
    department: "Immigration",
    description: "Managers & Executives for visa processing, immigration services, and ensuring compliance with international regulations.",
    skills: ["Visa Processing", "Immigration Law", "Documentation"]
  }
];

const benefits = [
  "Competitive salary packages",
  "Comprehensive health benefits",
  "Professional growth opportunities",
  "Flexible working arrangements",
  "Annual travel allowances",
  "Learning & development programs"
];

const getDepartmentColor = (department: string) => {
  switch (department.toLowerCase()) {
    case 'travel': return 'bg-green-100 text-green-600';
    case 'forex': return 'bg-blue-100 text-blue-600';
    case 'operations': return 'bg-purple-100 text-purple-600';
    case 'management': return 'bg-orange-100 text-orange-600';
    case 'immigration': return 'bg-indigo-100 text-indigo-600';
    default: return 'bg-gray-100 text-gray-600';
  }
};

async function apiRequest(method: string, path: string, data: any) {
  const res = await fetch(path, {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
    body: data ? JSON.stringify(data) : null,
  });
  return res;
}

export default function CareersSection() {
  const { toast } = useToast();

  const jobApplicationSchema = z.object({
    name: z.string().min(1, "Name is required"),
    email: z.string().email("Invalid email address"),
    position: z.string().min(1, "Position is required"),
    resumeUrl: z.string().optional(),
  });

  const form = useForm<JobApplicationData>({
    resolver: zodResolver(jobApplicationSchema),
    defaultValues: {
      name: "",
      email: "",
      position: "",
      resumeUrl: "",
    },
  });

  const applicationMutation = useMutation({
    mutationFn: async (data: JobApplicationData) => {
      const response = await fetch(buildApiUrl('api/careers/apply'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Application submitted successfully!",
        description: "We'll review your application and get back to you soon.",
      });
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to submit application",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: JobApplicationData) => {
    applicationMutation.mutate(data);
  };

  return (
    <section id="careers" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Join Our Team</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">Build your career with Travel Wings and be part of our growing success story</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {jobOpenings.map((job) => (
              <div key={job.id} className="bg-gray-50 p-6 rounded-2xl border hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{job.title}</h3>
                    <div className="flex items-center text-sm text-gray-600 space-x-4">
                      <span className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {job.location}
                      </span>
                      <span className="flex items-center">
                        <Briefcase className="w-4 h-4 mr-1" />
                        {job.type}
                      </span>
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {job.experience}
                      </span>
                    </div>
                  </div>
                  <Badge className={getDepartmentColor(job.department)}>
                    {job.department}
                  </Badge>
                </div>
                <p className="text-gray-600 mb-4">{job.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {job.skills.map((skill, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Apply Now
                  </Button>
                </div>
              </div>
            ))}

            <div className="text-center mt-8">
              <Button 
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
                onClick={() => window.open('/careers', '_self')}
              >
                View All Openings
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-600 to-purple-700 p-6 rounded-2xl text-white">
              <h3 className="text-xl font-bold mb-4">Why Work With Us?</h3>
              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 text-yellow-400 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Apply</h3>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input placeholder="Full Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input type="email" placeholder="Email Address" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="position"
                    render={({ field }) => (
                      <FormItem>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Position" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="travel-consultant">Travel Consultant</SelectItem>
                            <SelectItem value="forex-specialist">Forex Specialist</SelectItem>
                            <SelectItem value="marketing-manager">Marketing Manager</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Upload Resume (PDF, DOC)</p>
                    <Input 
                      type="file" 
                      accept=".pdf,.doc,.docx" 
                      className="hidden" 
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // In a real implementation, you would upload the file and get a URL
                          form.setValue("resumeUrl", file.name);
                        }
                      }}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={applicationMutation.isPending}
                  >
                    {applicationMutation.isPending ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}